import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  ClipboardList,
  FileCheck2,
  Landmark,
  Save,
  ShieldAlert,
  ShoppingCart,
} from "lucide-react";

import SummaryStrategyForm from "../components/bid-information/SummaryStrategyForm";
import EligibilityFrameworkForm from "../components/bid-information/EligibilityFrameworkForm";
import CommercialsSowForm from "../components/bid-information/CommercialsSowForm";
import FinancialEngineeringForm from "../components/bid-information/FinancialEngineeringForm";
import RiskGovernanceForm from "../components/bid-information/RiskGovernanceForm";

import {
  getBidInformation,
  saveBidInformationDraft,
  submitBidInformation,
} from "../api/auth";

const generateId = () => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const tabs = [
  {
    id: "summary",
    label: "Summary & Strategy",
    shortLabel: "Summary",
    icon: ClipboardList,
  },
  {
    id: "eligibility",
    label: "Eligibility Framework",
    shortLabel: "Eligibility",
    icon: FileCheck2,
  },
  {
    id: "commercials",
    label: "Commercials & SOW",
    shortLabel: "Commercials",
    icon: ShoppingCart,
  },
  {
    id: "financial",
    label: "Financial Engineering",
    shortLabel: "Financial",
    icon: Landmark,
  },
  {
    id: "risk",
    label: "Risk & Governance",
    shortLabel: "Risk",
    icon: ShieldAlert,
  },
];

const initialFormData = {
  summary: {
    opportunityId: "",
    opportunityDate: "",
    tenderReference: "",
    projectName: "",
    dueDateTime: "",
    accountManager: "",
    customerName: "",
    presalesLead: "",
    bidManager: "",
    bidType: "",
    supportedBy: "",
    competitiveBidCount: "",
    deliveryTimeline: "",
    winningStrategy: "",
    projectValue: "",
    paymentTerms: "",
    pbgPercentage: "",
    emdSelection: "",
    raModelApplicable: "",
    emdValue: "",
    emdType: "",
    preBidDate: "",
    clarificationDeadline: "",
    bidSubmissionDate: "",
    expectedAwardDate: "",
    goNoGoStatus: "",
    approvedNetMargin: "",
    marginJustification: "",

    competitors: [
      {
        bidderName: "",
        oemName: "",
      },
    ],

    contacts: [
      {
        contactType: "Customer 1",
        contactPerson: "",
        email: "",
        phone: "",
      },
      {
        contactType: "Customer 2",
        contactPerson: "",
        email: "",
        phone: "",
      },
      {
        contactType: "OEM 1",
        contactPerson: "",
        email: "",
        phone: "",
      },
      {
        contactType: "OEM 2",
        contactPerson: "",
        email: "",
        phone: "",
      },
      {
        contactType: "MSI",
        contactPerson: "",
        email: "",
        phone: "",
      },
      {
        contactType: "Consultant",
        contactPerson: "",
        email: "",
        phone: "",
      },
    ],
  },

  eligibility: {
    preQualification: [
      {
        id: generateId(),
        criteria:
          "Financial Turnover: Average annual turnover of last 3 financial years.",
        compliantDetails: "",
        status: "",
        remarks: "",
      },
      {
        id: generateId(),
        criteria:
          "Past Experience: Number of similar hybrid cloud/infra projects executed.",
        compliantDetails: "",
        status: "",
        remarks: "",
      },
      {
        id: generateId(),
        criteria:
          "Local Presence: Registered local office or support center requirements.",
        compliantDetails: "",
        status: "",
        remarks: "",
      },
      {
        id: generateId(),
        criteria:
          "Legal Status: Non-blacklisting certificate and positive Net Worth criteria.",
        compliantDetails: "",
        status: "",
        remarks: "",
      },
    ],

    technicalQualification: [
      {
        id: generateId(),
        parameter: "",
        proposedSolution: "",
        compliance: "",
        technicalRisk: "",
      },
    ],

    oemPreQualification: [
      {
        id: generateId(),
        criteria: "",
        compliantDetails: "",
        status: "",
        remarks: "",
      },
    ],

    scopeOfWork: [
      {
        id: generateId(),
        sow: "",
        ownership: "",
        manDays: "",
        cost: "",
      },
    ],
  },

  commercials: {
    bomItems: [
      {
        id: generateId(),
        category: "Hardware",
        itemDescription: "",
        oem: "",
        quantity: "",
        unitCost: "",
      },
    ],

    projectManagementCharges: "",
    miscellaneousPercentage: "10",
    implementationCharges: "",
    technicalCost: "",
    marginPercentage: "7",
    gstPercentage: "18",
  },

  financial: {
    projectBaseValue: "",
    contractDurationYears: "",
    emdBaseAmount: "",
    emdValidityDays: "",
    pbgPercentage: "",
    pbgValidityDays: "",
    upfrontCost: "",

    financeRows: [
      {
        id: generateId(),
        parameter: "EMD Type A: Demand Draft (DD)",
        fixedCostPercentage: "0",
        variableCostPercentage: "15",
        duration: "",
        selected: false,
      },
      {
        id: generateId(),
        parameter: "EMD Type B: Bank Guarantee (BG)",
        fixedCostPercentage: "1.5",
        variableCostPercentage: "9",
        duration: "",
        selected: true,
      },
      {
        id: generateId(),
        parameter: "EMD Type C: Fixed Deposit (FD)",
        fixedCostPercentage: "0",
        variableCostPercentage: "9",
        duration: "",
        selected: false,
      },
      {
        id: generateId(),
        parameter: "Performance Bank Guarantee (PBG)",
        fixedCostPercentage: "1.5",
        variableCostPercentage: "9",
        duration: "365",
        selected: true,
      },
      {
        id: generateId(),
        parameter: "GeM Portal Transaction Fees",
        fixedCostPercentage: "0.5",
        variableCostPercentage: "0",
        duration: "1",
        selected: true,
      },
      {
        id: generateId(),
        parameter: "Payment Terms: Quarterly Advance",
        fixedCostPercentage: "0",
        variableCostPercentage: "15",
        duration: "",
        selected: true,
      },
      {
        id: generateId(),
        parameter: "Payment Terms: Quarterly Arrear",
        fixedCostPercentage: "0",
        variableCostPercentage: "15",
        duration: "",
        selected: false,
      },
      {
        id: generateId(),
        parameter: "Payment Terms on Arrear",
        fixedCostPercentage: "0",
        variableCostPercentage: "15",
        duration: "3",
        selected: false,
      },
    ],

    transactionFlow: [
      {
        id: generateId(),
        level: "Level 1",
        entity: "Customer to Lead Partner",
        flowLogic: "Milestone Based",
        projectedCostFactor: "",
        remarks: "Direct client receivables",
      },
      {
        id: generateId(),
        level: "Level 2",
        entity: "Lead Partner to F1",
        flowLogic: "Back-to-Back Payments",
        projectedCostFactor: "",
        remarks: "Ensures minimized cash lock",
      },
      {
        id: generateId(),
        level: "Level 3",
        entity: "F1 to OEMs / Distributors",
        flowLogic: "Advance / Net 30/60 Days",
        projectedCostFactor: "",
        remarks: "",
      },
      {
        id: generateId(),
        level: "PMC",
        entity: "Project Management Charges",
        flowLogic: "Fixed 10% Layer",
        projectedCostFactor: "",
        remarks: "Internal governance allocation",
      },
    ],
  },

  risk: {
    monthlyExposure: "",
    quarterlyExposure: "",

    risks: [
      {
        id: generateId(),
        riskId: "RSK-01",
        classification: "Financial",
        description: "",
        impactTier: "",
        mitigationStrategy: "",
        owner: "Finance Lead",
      },
      {
        id: generateId(),
        riskId: "RSK-02",
        classification: "Technical",
        description: "",
        impactTier: "",
        mitigationStrategy: "",
        owner: "Pre-Sales Lead",
      },
      {
        id: generateId(),
        riskId: "RSK-03",
        classification: "Commercial",
        description: "",
        impactTier: "",
        mitigationStrategy: "",
        owner: "Cloud Architect",
      },
    ],
  },
};

export default function BidInformation() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const leadState = location.state || {};

  const [activeTab, setActiveTab] = useState("summary");

  const [formData, setFormData] = useState(() => ({
    ...initialFormData,

    summary: {
      ...initialFormData.summary,
      opportunityId: leadId || leadState.leadId || "",
      customerName: leadState.customerName || "",
      accountManager: leadState.accountManager || "",
      projectValue: leadState.projectValue || "",
    },
  }));

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [bidStatus, setBidStatus] = useState("not_started");

  useEffect(() => {
    const fetchBidInformation = async () => {
      if (!leadId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setErrorMessage("");

        const response = await getBidInformation(leadId);

        if (response.data?.success && response.data?.data) {
          const savedData = response.data.data;

          setFormData((previous) => ({
            ...previous,

            summary: {
              ...previous.summary,
              ...(savedData.summary || {}),
            },

            eligibility: {
              ...previous.eligibility,
              ...(savedData.eligibility || {}),
            },

            commercials: {
              ...previous.commercials,
              ...(savedData.commercials || {}),
            },

            financial: {
              ...previous.financial,
              ...(savedData.financial || {}),
            },

            risk: {
              ...previous.risk,
              ...(savedData.risk || {}),
            },
          }));

          setBidStatus(savedData.status || "draft");
        }
      } catch (error) {
        console.error("Error loading Bid Information:", error);

        setErrorMessage(
          error.response?.data?.message ||
            "Unable to load Bid Information."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchBidInformation();
  }, [leadId]);

  const isSubmitted = bidStatus === "submitted";

  const activeTabIndex = useMemo(
    () => tabs.findIndex((tab) => tab.id === activeTab),
    [activeTab]
  );

  const updateSection = (sectionName, updatedSection) => {
    setFormData((previous) => ({
      ...previous,

      [sectionName]:
        typeof updatedSection === "function"
          ? updatedSection(previous[sectionName])
          : updatedSection,
    }));
  };

  const handleSaveDraft = async () => {
    if (isSubmitted) {
      setErrorMessage("Submitted Bid Information cannot be edited.");
      return;
    }

    try {
      setSaving(true);
      setMessage("");
      setErrorMessage("");

      const response = await saveBidInformationDraft(
        leadId,
        formData
      );

      if (response.data?.success) {
        setBidStatus("draft");

        setMessage(
          response.data.message ||
            "Bid Information draft saved successfully."
        );
      } else {
        setErrorMessage(
          response.data?.message ||
            "Unable to save the draft."
        );
      }
    } catch (error) {
      console.error("Error saving Bid Information:", error);

      setErrorMessage(
        error.response?.data?.message ||
          "Unable to save Bid Information."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitBidInformation = async () => {
    if (isSubmitted) {
      setErrorMessage(
        "Bid Information has already been submitted."
      );
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to submit this Bid Information? You will not be able to edit it afterward."
    );

    if (!confirmed) return;

    try {
      setSubmitting(true);
      setMessage("");
      setErrorMessage("");

      const response = await submitBidInformation(
        leadId,
        formData
      );

      if (response.data?.success) {
        setBidStatus("submitted");

        setMessage(
          response.data.message ||
            "Bid Information submitted successfully."
        );
      } else {
        setErrorMessage(
          response.data?.message ||
            "Unable to submit Bid Information."
        );
      }
    } catch (error) {
      console.error(
        "Error submitting Bid Information:",
        error
      );

      setErrorMessage(
        error.response?.data?.message ||
          "Unable to submit Bid Information."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrevious = () => {
    if (activeTabIndex > 0) {
      setActiveTab(tabs[activeTabIndex - 1].id);

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const handleNext = () => {
    if (activeTabIndex < tabs.length - 1) {
      setActiveTab(tabs[activeTabIndex + 1].id);

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const renderActiveForm = () => {
    switch (activeTab) {
      case "summary":
        return (
          <SummaryStrategyForm
            data={formData.summary}
            onChange={(updatedData) =>
              updateSection("summary", updatedData)
            }
          />
        );

      case "eligibility":
        return (
          <EligibilityFrameworkForm
            data={formData.eligibility}
            onChange={(updatedData) =>
              updateSection("eligibility", updatedData)
            }
          />
        );

      case "commercials":
        return (
          <CommercialsSowForm
            data={formData.commercials}
            financialData={formData.financial}
            onChange={(updatedData) =>
              updateSection("commercials", updatedData)
            }
          />
        );

      case "financial":
        return (
          <FinancialEngineeringForm
            data={formData.financial}
            onChange={(updatedData) =>
              updateSection("financial", updatedData)
            }
          />
        );

      case "risk":
        return (
          <RiskGovernanceForm
            data={formData.risk}
            onChange={(updatedData) =>
              updateSection("risk", updatedData)
            }
          />
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600" />

          <p className="mt-4 text-sm font-medium text-slate-600">
            Loading Bid Information...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-[1600px] px-5 py-5 lg:px-8">
          <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
            <div className="flex items-start gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="mt-1 rounded-lg border border-slate-200 bg-white p-2 text-slate-600 transition hover:bg-slate-100"
                title="Go back"
              >
                <ArrowLeft size={20} />
              </button>

              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600">
                  Opportunity #{leadId}
                </p>

                <h1 className="mt-1 text-2xl font-bold text-slate-900">
                  Bid Information
                </h1>

                <p className="mt-1 text-sm text-slate-500">
                  {formData.summary.customerName ||
                    "Customer not available"}

                  {formData.summary.projectName
                    ? ` • ${formData.summary.projectName}`
                    : ""}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {message && (
                <p className="text-sm font-medium text-emerald-700">
                  {message}
                </p>
              )}

              {errorMessage && (
                <p className="text-sm font-medium text-red-600">
                  {errorMessage}
                </p>
              )}

              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={
                  saving ||
                  submitting ||
                  isSubmitted
                }
                className="inline-flex items-center gap-2 rounded-lg border border-blue-600 bg-white px-4 py-2.5 text-sm font-semibold text-blue-600 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Save size={17} />

                {saving
                  ? "Saving..."
                  : "Save Draft"}
              </button>

              <button
                type="button"
                onClick={handleSubmitBidInformation}
                disabled={
                  submitting ||
                  saving ||
                  isSubmitted
                }
                className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition ${
                  isSubmitted
                    ? "cursor-not-allowed bg-emerald-500"
                    : "bg-blue-600 hover:bg-blue-700"
                } disabled:opacity-70`}
              >
                <CheckCircle2 size={17} />

                {isSubmitted
                  ? "Bid Information Submitted"
                  : submitting
                  ? "Submitting..."
                  : "Submit Bid Information"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="sticky top-0 z-20 border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-[1600px] overflow-x-auto px-5 lg:px-8">
          <div className="flex min-w-max">
            {tabs.map((tab, index) => {
              const Icon = tab.icon;
              const isActive =
                activeTab === tab.id;
              const isCompleted =
                index < activeTabIndex;

              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() =>
                    setActiveTab(tab.id)
                  }
                  className={`relative flex min-w-[190px] items-center justify-center gap-2 border-b-2 px-5 py-4 text-sm font-semibold transition ${
                    isActive
                      ? "border-blue-600 bg-blue-50/60 text-blue-700"
                      : "border-transparent text-slate-500 hover:bg-slate-50 hover:text-slate-800"
                  }`}
                >
                  <span
                    className={`flex h-7 w-7 items-center justify-center rounded-full text-xs ${
                      isActive
                        ? "bg-blue-600 text-white"
                        : isCompleted
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle2 size={16} />
                    ) : (
                      index + 1
                    )}
                  </span>

                  <Icon size={17} />

                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-[1600px] px-5 py-7 lg:px-8">
        <div className="mb-5 rounded-xl border border-blue-100 bg-blue-50 px-5 py-4">
          <h2 className="font-semibold text-blue-950">
            {tabs[activeTabIndex]?.label}
          </h2>

          <p className="mt-1 text-sm text-blue-700">
            Complete this section and select Save Draft
            before leaving the opportunity.
          </p>
        </div>

        {isSubmitted && (
          <div className="mb-5 rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-4">
            <p className="font-semibold text-emerald-800">
              This Bid Information has been submitted.
            </p>

            <p className="mt-1 text-sm text-emerald-700">
              The form is now read-only and cannot be
              edited.
            </p>
          </div>
        )}

        <div
          className={
            isSubmitted
              ? "pointer-events-none opacity-75"
              : ""
          }
        >
          {renderActiveForm()}
        </div>

        <div className="mt-7 flex flex-col-reverse justify-between gap-3 border-t border-slate-200 pt-5 sm:flex-row">
          <button
            type="button"
            onClick={handlePrevious}
            disabled={activeTabIndex === 0}
            className="rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Previous Section
          </button>

          {activeTabIndex <
          tabs.length - 1 ? (
            <button
              type="button"
              onClick={handleNext}
              className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700"
            >
              Save and Continue
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSaveDraft}
              className="rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700"
            >
              Save All Sections
            </button>
          )}
        </div>
      </main>
    </div>
  );
}